import { groupLabelCode, MAX_BLANK_LABELS } from "@bt/domain";
import { useMutation } from "@tanstack/react-query";
import { MapPinIcon } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Field } from "@/components/app/field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { errorMessage } from "@/lib/errors";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";

export type LabelItem = RouterOutputs["labels"]["basket"][number];

/** One label in a list: its position, its code as people read it, its part and place. */
export function LabelRow({
  label,
  position,
  actions,
}: {
  label: LabelItem;
  position?: number;
  actions?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      {position !== undefined ? (
        <span className="w-7 shrink-0 text-right text-sm text-muted-foreground tabular">
          {position}
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-mono text-sm font-medium">{groupLabelCode(label.code)}</span>
          <span className="truncate text-sm">
            {label.part ? label.part.name : t("labels.blankLabel")}
          </span>
          {label.part?.primaryNumber ? (
            <span className="truncate font-mono text-xs text-muted-foreground">
              {label.part.primaryNumber}
            </span>
          ) : null}
        </div>
        {label.part?.locationPath.length ? (
          <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
            <MapPinIcon className="size-3 shrink-0" />
            <span className="truncate">{label.part.locationPath.join(" › ")}</span>
          </div>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
    </div>
  );
}

/** Says why a change to labels failed. */
export function useShowError() {
  const { t } = useTranslation();
  return (error: unknown) => toast.error(errorMessage(error, t));
}

/** "Add blank labels": N codes for no part yet, into the basket. */
export function AddBlankDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const id = useId();
  const [count, setCount] = useState("10");
  const showError = useShowError();
  const add = useMutation({
    ...trpc.labels.addBlank.mutationOptions(),
    onSuccess: (codes) => toast.success(t("labels.blankAdded", { count: codes.length })),
    onError: showError,
  });
  const value = Number(count);
  const valid = Number.isInteger(value) && value >= 1 && value <= MAX_BLANK_LABELS;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!valid) return;
            add.mutate({ count: value }, { onSuccess: () => onOpenChange(false) });
          }}
        >
          <DialogHeader title={t("labels.addBlank")} description={t("labels.addBlankBody")} />
          <DialogBody>
            <Field
              label={t("labels.count")}
              htmlFor={id}
              error={count && !valid ? t("labels.countHint", { max: MAX_BLANK_LABELS }) : undefined}
            >
              <Input
                id={id}
                inputMode="numeric"
                value={count}
                onChange={(event) => setCount(event.target.value.replace(/\D/g, ""))}
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={!valid || add.isPending}>
              {t("labels.addBlank")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
